import {
	ChartsEnum, GetChartsHookPropsType, SGChartDataType, useUrlParams,
} from '@aleph/shared-components';
import { gql, useQuery } from '@apollo/client';
import { MultiValue } from 'react-select';
import { divideCurrencyFields } from 'wb-reddit/app/routes/superGrid/common/constants';
import { useMemo } from 'react';
import { Stats } from 'wb-reddit/graphql/generated';
import { cloneDeep } from 'lodash';
import { SPOTIFY_SUPERGRID_STATISTICS_DATE_DIMENSIONS } from 'wb-spotify/app/routes/superGrid/statistics/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { getDateRangeForSupergridFromParams } from 'wb-spotify/app/routes/superGrid/utils';

interface StatItem extends Stats {
	label: string
	id: string
	units: {
		ctr: string
	}
}

type StatFieldName = keyof Omit<Stats, '__typename'>;

function preprocessStats(data: StatItem[], statKey: StatFieldName, breakdownType: string) {
	const dataDeepCopy = cloneDeep(data);

	// Handle genre breakdown: move OTHER_GENRE to otherItems
	if (breakdownType === 'genre') {
		const otherGenreIndex = dataDeepCopy.findIndex(item => item.id === 'OTHER_GENRE');

		if (otherGenreIndex !== -1) {
			// Remove OTHER_GENRE from main data and add it to the end
			const otherGenreItem = dataDeepCopy.splice(otherGenreIndex, 1)[0];
			dataDeepCopy.push(otherGenreItem);
		}
	}

	const items = dataDeepCopy.slice(0, 4);
	const otherItems = dataDeepCopy.slice(4);

	if (otherItems.length > 0) {
		const others = otherItems.reduce(
			(acc, item) => {
				acc[statKey] += item[statKey] ?? 0;

				return acc;
			},
			{
				[statKey]:  0,
				label:      statKey === 'ctr' ? 'Other (Avg)' : 'Other',
				id:         'Others',
				__typename: 'Stats',
			},
		);

		if (typeof others[statKey] === 'number') {
			// @ts-ignore: in the above if we check if value is number, this was failing the build step
			others[statKey] /= otherItems.length;
		}

		items.push(others as unknown as StatItem);
	}

	items.map(item => {
		if (statKey === 'ctr' && typeof item.ctr === 'number') {
			if (item.units) {
				item.units.ctr = '%';
			} else {
				item.units = {
					ctr: '%',
				};
			}
		}

		if (
			(statKey === 'spend' || statKey === 'cpc' || statKey === 'ecpm')
			&& typeof item[statKey] === 'number'
		) {
			// @ts-ignore: in the above if we check if value is number, this was failing the build step
			item[statKey] /= divideCurrencyFields[statKey];
		}

		return item;
	});

	return items;
}

const getTimeStatsQuery = (fields: MultiValue<string>) => gql`
	query spotifyGetTimeStats($filters: StatsFilter) {
		stats(limit: 300, filters: $filters) {
			items {
                ${fields?.join(',')}
                date
                id: date
				currency: adAccountCurrencyCode
            }
        }
	}
`;

const getBreakdownStatsQuery = (field: string | undefined, breakdownType: string) => gql`
	query getBreakdownStats($orderByColumn: StatsOrderByColumn!, $filters: StatsFilter) {
		stats(
			filters: $filters, 
			orderBy: [{ 
				direction: DESC, 
				column: $orderByColumn 
			}], 
			limit: 6, 
			query: "${breakdownType} IS NOT NULL"
		) {
			items {
				${field}
                label: ${breakdownType}
                id: ${breakdownType}
            }
        }
	}
`;

const useSpotifyCharts = (params: GetChartsHookPropsType): SGChartDataType => {
	const navigate = useNavigate();
	const { params: urlParams } = useUrlParams({ navigate, useParams });

	const fields = params.options?.timeChartOptions?.fields ?? [];

	const { data: timeData, loading: timeDataLoading } = useQuery(
		getTimeStatsQuery(fields),
		{
			skip:      params.chartType !== ChartsEnum.TIME || fields.length === 0,
			variables: {
				filters: {
					...urlParams?.filters,
					date: { value: getDateRangeForSupergridFromParams(urlParams) },
				},
			},
		},
	);

	const breakdownField = params.options?.breakdownChartOptions?.field as StatFieldName | undefined;

	const { data: breakdownData, loading: breakdownLoading } = useQuery(
		getBreakdownStatsQuery(breakdownField, params.chartType),
		{
			skip:      params.chartType === ChartsEnum.TIME || !breakdownField,
			variables: {
				...params.options?.breakdownChartOptions?.variables,
				filters: {
					...urlParams?.filters,
					date: (
						// @ts-ignore
						SPOTIFY_SUPERGRID_STATISTICS_DATE_DIMENSIONS.includes(params.chartType) || params.chartType === 'time'
							? { value: getDateRangeForSupergridFromParams(urlParams) }
							: undefined
					),
				},
			},
		},
	);

	return {
		timeData: useMemo(() => ({
			stats: {
				items: timeData?.stats?.items?.map(stat => ({
					...stat,
					spend: (stat.spend ?? 0),
					cpc:   (stat.cpc ?? 0),
					ecpm:  (stat.ecpm ?? 0),
					ctr:   (stat.ctr ?? 0),
					units: {
						ctr:   '%',
						spend: stat.currency,
						cpc:   stat.currency,
						ecpm:  stat.currency,
					},
				})) ?? [],
			},
		}), [timeData]),
		timeDataLoading,
		breakdownData: useMemo(
			() => ({
				stats: {
					items:
						breakdownField && breakdownData?.stats?.items
							? preprocessStats(breakdownData?.stats?.items, breakdownField, params.chartType)
							: [],
				},
			}),
			[breakdownData?.stats?.items, breakdownField],
		),
		breakdownLoading,
	};
};

export default useSpotifyCharts;
